import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { theme as antTheme } from 'antd';
import { THEME_MODES, isDarkMode, type ThemeMode } from '@/lib/theme';

export type { ThemeMode };

interface ThemeContextType {
  mode: ThemeMode;
  availableModes: readonly ThemeMode[];
  setTheme: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'bakedshift-theme';

function isValidMode(value: string | null): value is ThemeMode {
  return value !== null && (THEME_MODES as readonly string[]).includes(value);
}

function readStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return isValidMode(stored) ? stored : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(readStoredMode);

  useEffect(() => {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const setTheme = useCallback((next: ThemeMode) => {
    if (!(THEME_MODES as readonly string[]).includes(next)) return;
    setMode(next);
  }, []);

  // Legacy: kept for back-compat with callers that only flip light <-> dark.
  const toggleTheme = useCallback(() => {
    setMode((prev) => (isDarkMode(prev) ? 'light' : 'dark'));
  }, []);

  const value = useMemo<ThemeContextType>(
    () => ({
      mode,
      availableModes: THEME_MODES,
      setTheme,
      toggleTheme,
      isDark: isDarkMode(mode),
    }),
    [mode, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export const darkAlgorithm = antTheme.darkAlgorithm;
export const defaultAlgorithm = antTheme.defaultAlgorithm;
